import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

// ---------------------------------------------------------------------------
// ImageUrlDialog
//
// MUI Dialog for entering / editing an image URL and optional alt text.
// Validates that the URL uses https://.
// ---------------------------------------------------------------------------

interface ImageUrlDialogProps {
  open: boolean;
  initialSrc?: string;
  initialAlt?: string;
  onConfirm: (src: string, alt: string) => void;
  onCancel: () => void;
}

const HTTPS_RE = /^https:\/\/.+/i;

const ImageUrlDialog: React.FC<ImageUrlDialogProps> = ({
  open,
  initialSrc = '',
  initialAlt = '',
  onConfirm,
  onCancel,
}) => {
  const [src, setSrc] = useState(initialSrc);
  const [alt, setAlt] = useState(initialAlt);

  // Reset fields when dialog opens
  useEffect(() => {
    if (open) {
      setSrc(initialSrc);
      setAlt(initialAlt);
    }
  }, [open, initialSrc, initialAlt]);

  const isValid = HTTPS_RE.test(src.trim());

  const handleConfirm = useCallback(() => {
    if (!isValid) return;
    onConfirm(src.trim(), alt.trim());
  }, [src, alt, isValid, onConfirm]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{initialSrc ? 'Edit Image URL' : 'Insert Image from URL'}</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
      >
        <TextField
          label="Image URL"
          placeholder="https://example.com/image.png"
          value={src}
          onChange={(e) => setSrc(e.target.value)}
          error={src.length > 0 && !isValid}
          helperText={src.length > 0 && !isValid ? 'URL must start with https://' : undefined}
          fullWidth
          autoFocus
        />
        <TextField
          label="Alt text (optional)"
          placeholder="Description of the image"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" disabled={!isValid} onClick={handleConfirm}>
          {initialSrc ? 'Update' : 'Insert'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUrlDialog;
